import { useState, useEffect } from "react";
import { api, errMsg, fmtPrice } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function AccountPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [learning, setLearning] = useState([]);
  const [reason, setReason] = useState("");
  const [refCourse, setRefCourse] = useState("");

  const load = () => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/refunds").then((r) => setRefunds(r.data)).catch(() => {});
    api.get("/my-learning").then((r) => setLearning(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const requestRefund = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/refunds", { course_id: refCourse, reason });
      toast.success(data.auto_flagged ? "Request submitted — flagged for manual review (≥20% watched)" : "Refund request submitted");
      setReason(""); setRefCourse("");
      load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const statusColor = { pending: "text-[var(--forge-gold)]", approved: "text-[var(--forge-green)]", denied: "text-red-400" };

  return (
    <div className="max-w-4xl mx-auto px-4 pt-28 pb-12 space-y-10">
      <div>
        <h1 className="font-display font-bold text-2xl" data-testid="account-title">ACCOUNT</h1>
        <p className="text-[var(--forge-dim)] text-sm mt-1">{user?.name} · {user?.email}</p>
      </div>

      <section>
        <h2 className="font-display font-bold text-base mb-4">ORDER HISTORY</h2>
        {orders.length === 0 ? <p className="text-sm text-[var(--forge-dim)]" data-testid="no-orders">No orders yet.</p> : (
          <div className="space-y-2">
            {orders.map((o) => (
              <div key={o.id} className="forge-card !transform-none p-4" data-testid={`order-${o.id}`}>
                <div className="flex justify-between text-sm">
                  <span className="text-[var(--forge-dim)]">{new Date(o.created_at).toLocaleDateString()} · {o.payment_method}</span>
                  <span className="font-display font-bold text-[var(--forge-green)]">{o.currency === "EUR" ? "€" : "$"}{o.total.toFixed(2)}</span>
                </div>
                {o.items.map((it) => <p key={it.course_id} className="text-sm mt-1">{it.title}</p>)}
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display font-bold text-base mb-4">REQUEST A REFUND</h2>
        {learning.length === 0 ? <p className="text-sm text-[var(--forge-dim)]">Enroll in a course first.</p> : (
          <form onSubmit={requestRefund} className="forge-card !transform-none p-4 space-y-3" data-testid="refund-form">
            <select data-testid="refund-course-select" required value={refCourse} onChange={(e) => setRefCourse(e.target.value)} className="input-dark text-sm">
              <option value="">Select course…</option>
              {learning.map((l) => <option key={l.course.id} value={l.course.id}>{l.course.title} ({l.progress_pct}% watched)</option>)}
            </select>
            <textarea data-testid="refund-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" className="input-dark text-sm" rows={2} />
            <button className="btn-outline text-sm" data-testid="refund-submit-btn">SUBMIT REQUEST</button>
            <p className="text-xs text-[var(--forge-dim)]">Requests with ≥20% watch progress are flagged for manual review.</p>
          </form>
        )}
        {refunds.length > 0 && (
          <div className="mt-4 space-y-2">
            {refunds.map((r) => (
              <div key={r.id} className="forge-card !transform-none p-3 flex justify-between items-center text-sm" data-testid={`refund-${r.id}`}>
                <div>
                  <p>{r.course_title}</p>
                  <p className="text-xs text-[var(--forge-dim)]">{r.watch_pct}% watched {r.auto_flagged && "· flagged for review"}</p>
                </div>
                <span className={`font-display uppercase text-xs ${statusColor[r.status]}`} data-testid={`refund-status-${r.id}`}>{r.status}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
